// What makes a trip a trip.
//
// A trip has no row of its own on either side of the app: an upcoming trip is
// a set of TaskPogoTrip legs, a logged trip is a set of TaskPogoCompletedTask
// entries, and in both cases the trip is whatever those records agree they
// belong to. This module owns the one rule that decides that, so the two sides
// cannot drift apart -- they used to be two byte-identical copies of a
// trim-and-lowercase, which is exactly how they once came to disagree on case.
//
// Originally a record agreed by NAME, and that is the whole reason this module
// exists: two genuinely different trips that happened to share a name were one
// trip by every definition in the codebase -- one group, one page, one URL, one
// share row, one calendar colour -- so a handful of unrelated day cruises all
// called "Day" rolled up into a single twelve-leg trip nobody took.
//
// So a trip now has an identity: an opaque `tripGroupId` stamped on each record.
// This is the move the gazetteer already made, and deliberately so -- a
// waypoint binds to a place row by `placeId` while its stored `name` is a
// display snapshot (see TripRoute.resolveRouteAgainstPlaces). Here the id binds
// and the name is a label, which is what lets five trips be called "Day" and
// what lets one trip be renamed without becoming a different trip.
//
// The NAME key is kept as a fallback rather than migrated away, because
// records written before the id existed still have to group, address and share
// exactly as they did. See `tripKeyFor` for the precedence and `findTripGroup`
// for how a URL minted under the old rule still resolves.

// The four key shapes. A key is a string so it can be a URL segment, a React
// key, a Map key and a stored TaskPogoTripShare.groupKey without conversion.
export const KEY_TRIP = 'g:';   // a trip with an explicit id -- legs or entries
export const KEY_NAME = 'n:';   // a trip identified only by its lowercased name
export const KEY_LEG = 't:';    // one unnamed TaskPogoTrip leg, standing alone
export const KEY_ENTRY = 'e:';  // one unfiled CompletedTask, standing alone

// The comparison key for a trip name: trimmed and lowercased, because the name
// is hand-typed on every record and "Bay Tour" and "bay tour " are one name to
// everyone but a string comparison. '' for a blank name, which never groups --
// a record with no trip name is not "the trip called nothing".
export function normalizeTripName(name) {
  return String(name ?? '').trim().toLowerCase();
}

// The key of the trip a record belongs to. One rule, both tables:
//
//   blank name -> the record stands alone   (KEY_LEG / KEY_ENTRY + its own id)
//   id set     -> the trip's own identity   (KEY_TRIP + tripGroupId)
//   otherwise  -> the name, as it always was (KEY_NAME + normalized name)
//
// The blank-name branch comes FIRST, and that ordering is the invariant: a
// record with no name is not in a trip, whatever id it happens to be carrying.
// Nothing should ever write that combination -- `tripIdentityDefaults` refuses
// to, and the Trip Name box clears the id when the box is emptied -- so this is
// the backstop for a row that acquired one anyway, not a supported state.
//
// Note what is NOT here: an id-less record never joins an id-bearing trip by
// name. That is the difference between this and the gazetteer's rule, which
// does bootstrap by name, and it is deliberate. There, a name match is the only
// way a route written before `placeId` existed could ever bind. Here the
// backfill binds those records instead, and letting a name match join a trip
// would mean typing "Day" silently joined one of the five trips already called
// that -- reintroducing the bug at the one keystroke where it matters most.
// Legacy ADDRESSING is handled by `findTripGroup`, which is a different
// question and answered in a different place.
export function tripKeyFor({ name, tripGroupId, ownId, alonePrefix }) {
  if (normalizeTripName(name) === '') return alonePrefix + (ownId ?? '');
  const id = String(tripGroupId ?? '').trim();
  if (id !== '') return KEY_TRIP + id;
  return KEY_NAME + normalizeTripName(name);
}

// True for a key other records can JOIN. A KEY_LEG / KEY_ENTRY key belongs to
// one row and can never gain a second, which is what gates "Add next leg" and
// the leg-date cascade.
//
// The name is now a shade narrow -- a KEY_TRIP group is identified rather than
// named -- but every call site means exactly this question, so it keeps the
// name it has always had rather than renaming twenty call sites to say the
// same thing.
export function isNamedGroupKey(key) {
  return typeof key === 'string' && (key.startsWith(KEY_TRIP) || key.startsWith(KEY_NAME));
}

// True for a key carrying a trip's own id, i.e. one a rename cannot move.
export function isTripGroupIdKey(key) {
  return typeof key === 'string' && key.startsWith(KEY_TRIP);
}

// The (lowercased) name encoded in a KEY_NAME key, or null for any other shape.
// Callers with a loaded group should prefer its `name` -- that's the first-seen
// spelling -- and fall back to this when the records haven't arrived yet. A
// KEY_TRIP key carries no name at all, which is why a page keyed by one has to
// get its heading from the records rather than from the URL.
export function groupNameFromKey(key) {
  return typeof key === 'string' && key.startsWith(KEY_NAME) ? key.slice(KEY_NAME.length) : null;
}

// The id encoded in a KEY_TRIP key, or null for any other shape.
export function tripGroupIdFromKey(key) {
  return isTripGroupIdKey(key) ? key.slice(KEY_TRIP.length) : null;
}

// The identity fields to stamp on a record being created into a trip, given
// what the caller knows about that trip.
//
// The invariant it exists to keep is `tripKeyFor`'s: never an id without a
// name. A group whose legs were all deleted still has a key in the URL but no
// name to give, and a record stamped with the id and no name would key as
// standing alone while claiming to belong somewhere.
export function tripIdentityDefaults({ name, tripGroupId } = {}) {
  const label = name == null ? '' : String(name).trim();
  if (label === '') return {};
  const id = String(tripGroupId ?? '').trim();
  return id === '' ? { name: label } : { name: label, tripGroupId: id };
}

// A fresh trip id.
//
// jsdom 16.7 -- what CRA 5's Jest runs -- has no `crypto.randomUUID`, so it
// cannot be called unguarded or every test that mints an id throws. Node 22 and
// every browser the app supports have it; the fallback is exercised only by the
// test environment, where the collision space of one item's trips is not
// something worth doing arithmetic about.
export function newTripGroupId() {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `tg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// Every group `key` could be addressing, best first.
//
// An exact match is every key minted since ids existed. A LEGACY KEY_NAME key
// -- a bookmark, a mailed URL, a TaskPogoTripShare row written before the
// backfill -- now matches nothing, because the trip it named carries an id: so
// fall back to matching on the name. This is where the name bootstrap that
// `tripKeyFor` deliberately refuses does belong, because addressing an existing
// trip and deciding which trip a record joins are different questions: guessing
// wrong here shows the user a page, guessing wrong there rewrites their data.
//
// A stale KEY_TRIP key never falls back. An id that resolves to nothing means
// the trip was deleted, and answering with something merely spelled the same
// would be a different trip wearing its name.
//
// More than one candidate means a legacy name key that has since been SPLIT --
// exactly the several-trips-called-"Day" case this whole change exists for.
// `compare` orders them; callers pass sortTripGroups so the answer is the order
// the trips list already puts trips in, rather than one invented here.
export function tripGroupsForKey(groups, key, { compare } = {}) {
  const all = groups || [];
  const exact = all.filter((group) => group?.key === key);
  if (exact.length > 0) return exact;
  const name = groupNameFromKey(key);
  if (name === null) return [];
  const byName = all.filter(
    (group) => isTripGroupIdKey(group?.key) && normalizeTripName(group?.name) === name
  );
  return compare ? compare(byName) : byName;
}

// The group `key` addresses, or null. See tripGroupsForKey for the rules; this
// is the one-answer form every call site that just needs a group should use.
export function findTripGroup(groups, key, options) {
  const all = tripGroupsForKey(groups, key, options);
  return all.length > 0 ? all[0] : null;
}
